import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { endpoints } from '../services/endpoints';
import { AuthShowcase } from '../components/AuthShowcase';
import { usePreferences } from '../hooks/usePreferences';
import { Button, Input, cx } from '../components/ui';

type Step = 'identify' | 'verify' | 'done';

interface SentInfo {
  sentToMasked: string | null;
  channel: 'EMAIL' | 'SMS';
  expiresInMinutes: number;
  devCode?: string;
  devNote?: string;
}

/**
 * Two-step reset: prove you can receive a code, then set a new password.
 *
 * Step one always advances, even for an identifier that has no account — the
 * API deliberately cannot say whether it exists, so the UI must not either.
 * A wrong email simply never receives a code.
 */
export function ForgotPasswordPage() {
  const navigate = useNavigate();
  const { t } = usePreferences();
  const [step, setStep] = useState<Step>('identify');
  const [identifier, setIdentifier] = useState('');
  const [sent, setSent] = useState<SentInfo | null>(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const requestCode = async (e: FormEvent) => {
    e.preventDefault();
    if (identifier.trim().length < 3) {
      setError(t('Give us something to work with — an email or phone.', 'Kuch to do kaam karne ke liye — email ya phone.'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const res = await endpoints.auth.forgotPassword(identifier.trim());
      setSent(res);
      setStep('verify');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('Could not send the code — the server is having a moment.', 'Code nahi bheja gaya — server ka mood off hai.'),
      );
    } finally {
      setBusy(false);
    }
  };

  const submitReset = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await endpoints.auth.resetPassword(identifier.trim(), code.trim(), password);
      setStep('done');
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('Could not reset the password — try that code again.', 'Password reset nahi hua — code dobara try karo.'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-surface">
      <AuthShowcase />

      <div className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-sm">
          <div className="mb-8 text-center">
            <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-brand font-mono text-lg font-bold text-surface">
              ⌘
            </div>
            <h1 className="font-display text-[22px] font-semibold tracking-[-0.01em] text-content">
              {step === 'done' ? t('Password changed', 'Password badal gaya') : t('Forgot your password?', 'Password bhool gaye?')}
            </h1>
            <p className="mt-1.5 text-sm text-content-muted">
              {step === 'identify' &&
                t("Enter your email or phone — we'll send a 6-digit code.", 'Email ya phone daalo — 6-digit code bhej denge.')}
              {step === 'verify' && t('Enter the code and set a new password.', 'Code daalo aur naya password set karo.')}
              {step === 'done' && t('Sign in with your new password.', 'Naye password se sign in karo.')}
            </p>
          </div>

          {/* Step indicator — two steps, so show them rather than hide the shape */}
          {step !== 'done' && (
            <div className="mb-4 flex items-center gap-2">
              {(['identify', 'verify'] as const).map((s, i) => (
                <div key={s} className="flex flex-1 items-center gap-2">
                  <span
                    className={cx(
                      'flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold',
                      step === s || (s === 'identify' && step === 'verify')
                        ? 'bg-brand text-surface'
                        : 'bg-surface-sunken text-content-subtle',
                    )}
                  >
                    {i + 1}
                  </span>
                  <span
                    className={cx(
                      'h-0.5 flex-1 rounded-full',
                      step === 'verify' && i === 0 ? 'bg-brand' : 'bg-surface-sunken',
                    )}
                  />
                </div>
              ))}
            </div>
          )}

          {step === 'identify' && (
            <form onSubmit={requestCode} className="card animate-fade-up space-y-4 p-6" noValidate>
              <div>
                <label htmlFor="identifier" className="mb-1.5 block text-[13px] font-medium text-content">
                  {t('Email or phone', 'Email ya phone')}
                </label>
                <Input
                  id="identifier"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder={t('you@example.com or 9876543210', 'you@example.com ya 9876543210')}
                  autoComplete="username"
                />
                <p className="mt-1.5 text-xs text-content-subtle">
                  {t(
                    "Phone only rings a bell if you've already added it to your account.",
                    'Phone tabhi kaam karega jab account mein pehle se add ho.',
                  )}
                </p>
              </div>

              {error && (
                <div className="rounded-lg border border-hard/30 bg-hard/10 px-3 py-2 text-[13px] text-hard">
                  {error}
                </div>
              )}

              <Button type="submit" variant="primary" loading={busy} className="w-full">
                {t('Send code', 'Code bhejo')}
              </Button>

              <p className="text-center text-[13px] text-content-muted">
                <Link to="/login" className="font-medium text-brand hover:underline">
                  {t('Back to sign in', 'Wapas sign in par')}
                </Link>
              </p>
            </form>
          )}

          {step === 'verify' && (
            <form onSubmit={submitReset} className="card animate-fade-up space-y-4 p-6" noValidate>
              <div className="rounded-lg border border-line bg-surface-sunken px-3 py-2.5">
                <p className="text-[13px] text-content-muted">
                  {sent?.sentToMasked ? (
                    <>
                      {t('Code sent to', 'Code bhej diya')}{' '}
                      <span className="font-mono text-content">{sent.sentToMasked}</span>.
                    </>
                  ) : (
                    t(
                      "If that account exists, a code is already on its way. If not… well, this message looks the same either way.",
                      'Agar account hai, to code nikal chuka hai. Nahi hai to bhi yehi message dikhega — hum bata nahi sakte.',
                    )
                  )}{' '}
                  {t(
                    `Don't let it ghost you — valid for ${sent?.expiresInMinutes} minutes.`,
                    `Jaldi karo — ${sent?.expiresInMinutes} minute mein ye ghost ho jayega.`,
                  )}
                </p>
              </div>

              {/* Console driver: show the code so the flow is usable with no SMS account */}
              {sent?.devCode && (
                <div className="rounded-lg border border-medium/30 bg-medium/10 px-3 py-2.5">
                  <p className="text-[11px] font-semibold uppercase tracking-wider text-medium">
                    Development
                  </p>
                  <p className="mt-1 font-mono text-xl font-semibold tracking-[0.3em] text-content">
                    {sent.devCode}
                  </p>
                  <p className="mt-1 text-[11px] leading-5 text-content-muted">{sent.devNote}</p>
                </div>
              )}

              <div>
                <label htmlFor="code" className="mb-1.5 block text-[13px] font-medium text-content">
                  {t('6-digit code', '6-digit code')}
                </label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  className="text-center font-mono text-lg tracking-[0.4em]"
                />
              </div>

              <div>
                <label htmlFor="new-password" className="mb-1.5 block text-[13px] font-medium text-content">
                  {t('New password', 'Naya password')}
                </label>
                <Input
                  id="new-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('At least 8 characters', 'Kam se kam 8 characters')}
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <div className="rounded-lg border border-hard/30 bg-hard/10 px-3 py-2 text-[13px] text-hard">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                loading={busy}
                disabled={code.length !== 6 || password.length < 8}
                className="w-full"
              >
                {t('Reset password', 'Password reset karo')}
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('identify');
                  setCode('');
                  setError(null);
                }}
                className="w-full text-center text-[13px] text-content-muted hover:text-content"
              >
                {t('Try a different email/phone', 'Doosra email/phone try karo')}
              </button>
            </form>
          )}

          {step === 'done' && (
            <div className="card animate-fade-up space-y-4 p-6 text-center">
              <div className="mx-auto flex h-11 w-11 items-center justify-center rounded-full bg-easy/15 text-xl text-easy">
                ✓
              </div>
              <p className="text-[13px] leading-6 text-content-muted">
                {t(
                  "Password updated. Every old session just got logged out — if someone else was in, they aren't anymore. Awkward for them.",
                  'Password update ho gaya. Purani saari sessions bahar — agar koi aur andar tha, ab nahi hai. Uske liye thoda awkward.',
                )}
              </p>
              <Button variant="primary" className="w-full" onClick={() => navigate('/login')}>
                {t('Sign in', 'Sign in karo')}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
